import Lottie from 'lottie-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { feedbackApi } from '@/api/feedback';
import { Star1, Spinner, Lock } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useModalContext } from '@/components/ui/modal-provider';
import { Textarea } from '@/components/ui/textarea';
import sadStarAnimation from '@/pages/assets/animations/grade-1-sad-star.json';
import regularStarAnimation from '@/pages/assets/animations/grade-2-regular-star.json';
import smilyStarAnimation from '@/pages/assets/animations/grade-3-smily-star.json';
import happyStarAnimation from '@/pages/assets/animations/grade-4-happy-star.json';
import loveStarAnimation from '@/pages/assets/animations/grade-5-love-star.json';

export const FeedbackModal: React.FC = () => {
    const { closeModal } = useModalContext();
    const [rating, setRating] = useState<number>(0);
    const [text, setText] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hoveredRating, setHoveredRating] = useState<number>(0);
    const starAnimations: Record<number, object> = {
        1: sadStarAnimation,
        2: regularStarAnimation,
        3: smilyStarAnimation,
        4: happyStarAnimation,
        5: loveStarAnimation,
    };
    const activeStar = hoveredRating || rating;

    const handleSubmit = async () => {
        if (rating === 0) {
            toast.error('Por favor, selecione uma avaliação.');
            return;
        }

        setIsSubmitting(true);
        try {
            await feedbackApi.sendFeedback({ rating, text });
            toast.success('Feedback enviado com sucesso! Obrigado.');
            closeModal();
        } catch (error) {
            console.error('Error sending feedback:', error);
            toast.error('Erro ao enviar feedback. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label>Avaliação</Label>
                <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                        const isActive = activeStar === star;

                        return (
                            <button
                                key={star}
                                type="button"
                                className="group inline-flex size-10 items-center justify-center align-middle focus:outline-none"
                                onMouseEnter={() => setHoveredRating(star)}
                                onMouseLeave={() => setHoveredRating(0)}
                                onClick={() => setRating(star)}
                            >
                                <div className="relative inline-flex size-10 origin-center items-center justify-center align-middle leading-none transition-transform group-hover:scale-110">
                                    {isActive && (
                                        <div className="pointer-events-none absolute inset-0 flex -translate-y-px items-center justify-center">
                                            <Lottie
                                                animationData={starAnimations[star]}
                                                loop={true}
                                                className="size-[125%]"
                                            />
                                        </div>
                                    )}
                                    <Star1
                                        className={`size-10 transition-opacity ${
                                            star <= (hoveredRating || rating)
                                                ? 'fill-yellow-400 text-yellow-400'
                                                : 'text-muted-foreground'
                                        } ${isActive ? 'invisible' : 'visible'}`}
                                    />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="observations">Observações *</Label>
                <Textarea
                    required
                    id="observations"
                    placeholder="Conte-nos o que você achou..."
                    value={text}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setText(e.target.value)
                    }
                    rows={4}
                    className="resize-none"
                />
            </div>

            <div className="flex items-center justify-center gap-2 px-4 text-center text-xs text-muted-foreground">
                <Lock className="size-3 flex-shrink-0" />
                <p>Seu feedback é anônimo e será usado para melhorar a plataforma.</p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting || rating === 0}>
                    {isSubmitting ? (
                        <>
                            <Spinner className="animate-spin" />
                            Enviando...
                        </>
                    ) : (
                        'Enviar Feedback'
                    )}
                </Button>
            </div>
        </div>
    );
};
